import classNames from 'classnames/bind';
import React, { useMemo } from 'react';

import styles from './EKFStatus.module.scss';
import MiniTable from './MiniTable';

import { MAVLINK_MESSAGE_IDS as MESSAGE } from '@/define/messageId';
import useRobotTelemetry from '@/hooks/useRobotTelemetry';

const cx = classNames.bind(styles);

const EKFStatus = ({ data: robotId }) => {
  const ekfStatusReport = useRobotTelemetry(robotId, MESSAGE.EKF_STATUS_REPORT);

  const gaugeValues = useMemo(() => {
    return {
      velocity: ekfStatusReport?.velocityVariance ?? 0,
      positionHoriz: ekfStatusReport?.posHorizVariance ?? 0,
      positionVert: ekfStatusReport?.posVertVariance ?? 0,
      compass: ekfStatusReport?.compassVariance ?? 0,
      terrain: ekfStatusReport?.terrainAltVariance ?? 0,
    };
  }, [ekfStatusReport]);

  const flagValues = useMemo(() => {
    if (!ekfStatusReport) return {};

    const flagBits = Array.from(ekfStatusReport.flags.toString(2).padStart(10, '0'), (flag) => flag === '1');
    const flagNames = [
      'predPosHorizAbs',
      'predPosHorizRel',
      'constPosMode',
      'posVertAgl',
      'posVertAbs',
      'posHorizAbs',
      'posHorizRel',
      'velocityVert',
      'velocityHoriz',
      'attitude',
    ];

    const nextFlagValues = {};
    flagNames.forEach((name, index) => {
      nextFlagValues[name] = flagBits[index] ? 'On' : 'Off';
    });

    return nextFlagValues;
  }, [ekfStatusReport]);

  return (
    <div className={cx('container')}>
      <div className={cx('gauges')}>
        <div className={cx('item')}>
          <div className={cx('label')}>Velocity</div>
          <div className={cx('gauge')}>
            <div className={cx('bar')} style={{ width: `${gaugeValues.velocity * 100}%` }} />
            <div className={cx(['caution', 'v50'])} />
            <div className={cx(['caution', 'v80'])} />
            <div className={cx('value')}>{gaugeValues.velocity.toFixed(3)}</div>
          </div>
        </div>
        <div className={cx('item')}>
          <div className={cx('label')}>Horizontal Position</div>
          <div className={cx('gauge')}>
            <div className={cx('bar')} style={{ width: `${gaugeValues.positionHoriz}%` }} />
            <div className={cx(['caution', 'v50'])} />
            <div className={cx(['caution', 'v80'])} />
            <div className={cx('value')}>{gaugeValues.positionHoriz.toFixed(3)}</div>
          </div>
        </div>
        <div className={cx('item')}>
          <div className={cx('label')}>Vertical Position</div>
          <div className={cx('gauge')}>
            <div className={cx('bar')} style={{ width: `${gaugeValues.positionVert}%` }} />
            <div className={cx(['caution', 'v50'])} />
            <div className={cx(['caution', 'v80'])} />
            <div className={cx('value')}>{gaugeValues.positionVert.toFixed(3)}</div>
          </div>
        </div>
        <div className={cx('item')}>
          <div className={cx('label')}>Compass</div>
          <div className={cx('gauge')}>
            <div className={cx('bar')} style={{ width: `${gaugeValues.compass}%` }} />
            <div className={cx(['caution', 'v50'])} />
            <div className={cx(['caution', 'v80'])} />
            <div className={cx('value')}>{gaugeValues.compass.toFixed(3)}</div>
          </div>
        </div>
        <div className={cx('item')}>
          <div className={cx('label')}>Terrain</div>
          <div className={cx('gauge')}>
            <div className={cx('bar')} style={{ width: `${gaugeValues.terrain}%` }} />
            <div className={cx(['caution', 'v50'])} />
            <div className={cx(['caution', 'v80'])} />
            <div className={cx('value')}>{gaugeValues.terrain.toFixed(3)}</div>
          </div>
        </div>
      </div>
      <MiniTable
        title="Flags"
        data={[
          { label: 'Attitude', value: flagValues.attitude },
          { label: 'Horizontal Velocity', value: flagValues.velocityHoriz },
          { label: 'Vertical Velocity', value: flagValues.velocityVert },
          { label: 'Horizontal Position (Relative)', value: flagValues.posHorizRel },
          { label: 'Horizontal Position (Absolute)', value: flagValues.posHorizAbs },
          { label: 'Vertical Position (Absolute)', value: flagValues.posVertAbs },
          { label: 'Vertical Position (Above Ground)', value: flagValues.posVertAgl },
          { label: 'Constant Position Mode', value: flagValues.constPosMode },
          { label: 'Predicted Horizontal Position (Relative)', value: flagValues.predPosHorizRel },
          { label: 'Predicted Horizontal Position (Absolute)', value: flagValues.predPosHorizAbs },
        ]}
      />
    </div>
  );
};

export default EKFStatus;
